import { useState, useEffect } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, closestCorners } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Plus, MoreHorizontal, GripVertical, Check, X } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useTimer } from "@/contexts/TimerContext";
import { TaskProperties, defaultTaskCfg } from "@/lib/taskProperties";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TaskQuickCreate } from "@/components/tasks/TaskQuickCreate";
import { AppIcon } from "@/lib/appearanceIcons";
import { AppearanceDialog } from "@/components/appearance/AppearanceDialog";

const PRIORITY_DOT = { low: "bg-muted-foreground/30", normal: "bg-primary/60", high: "bg-warning", urgent: "bg-destructive" };
const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);
const slugify = (s) => String(s || "").toLowerCase().replace(/\s+/g, "-");
const newId = () => (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
const CARD_MAX = { compact: 3, comfortable: 5, detailed: 20 };

function Card({ t, onOpen, cfg, catName, cats, members, running }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: t.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const dens = cfg?.density || "comfortable";
  return (
    <div ref={setNodeRef} style={style} data-testid={`board-card-${t.id}`}
      onClick={() => !isDragging && onOpen(t.id)}
      className={cn("group space-y-2 rounded-xl border border-border/70 bg-card p-3 shadow-soft transition-shadow hover:shadow-lift", isDragging && "opacity-60")}>
      <div className="flex items-start gap-2">
        <button {...attributes} {...listeners} onClick={(e) => e.stopPropagation()} data-testid={`drag-${t.id}`}
          className="mt-0.5 cursor-grab text-muted-foreground/50 hover:text-foreground active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>
        <span className={cn("mt-1 h-2 w-2 shrink-0 rounded-full", PRIORITY_DOT[t.priority] || PRIORITY_DOT.normal)} title={`Priority: ${t.priority || "normal"}`} />
        <span className="min-w-0 flex-1 text-[15px] font-medium leading-snug text-foreground">{t.title}</span>
      </div>
      <div className="pl-6">
        <TaskProperties t={t} ctx="board" density={dens} cfg={cfg} catName={catName} cats={cats} members={members} running={running} max={CARD_MAX[dens] || 5} />
      </div>
    </div>
  );
}

function Column({ stage, items, onOpen, onAdd, menu, renaming, renameValue, onRenameChange, onRenameCommit, onRenameCancel, cardProps }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col::${stage.id}` });
  const slug = slugify(stage.name);
  return (
    <div className={cn("flex max-h-[calc(100dvh-230px)] min-w-[260px] max-w-[360px] flex-1 flex-col rounded-2xl border border-border/80 bg-muted/20 p-3 shadow-xs transition-colors", isOver && "border-accent bg-accent-subtle/40 ring-2 ring-accent/20")}
      data-testid={`stage-col-${slug}`}>
      <div className="mb-3 flex items-center justify-between border-b border-border/50 px-1 pb-2">
        {renaming ? (
          <div className="flex items-center gap-1">
            <Input autoFocus value={renameValue} onChange={(e) => onRenameChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onRenameCommit(); if (e.key === "Escape") onRenameCancel(); }}
              className="h-7 w-36 text-xs" data-testid={`stage-rename-input-${slug}`} />
            <button onClick={onRenameCommit} data-testid={`stage-rename-save-${slug}`} className="rounded p-1 text-success hover:bg-muted"><Check className="h-3.5 w-3.5" /></button>
            <button onClick={onRenameCancel} className="rounded p-1 text-muted-foreground hover:bg-muted"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" style={stage.colour ? { color: stage.colour } : undefined}>
            <AppIcon name={stage.icon} className="h-3.5 w-3.5" />
            {stage.name}<span className="rounded-full border border-border/50 bg-secondary px-1.5 text-[10px] text-secondary-foreground" data-testid={`stage-count-${slug}`}>{items.length}</span>
          </span>
        )}
        <div className="flex items-center gap-0.5">{onAdd}{menu}</div>
      </div>
      <div ref={setNodeRef} className="flex-1 space-y-2 overflow-y-auto">
        <SortableContext items={items.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {items.length === 0 ? <p className="px-1 py-6 text-center text-xs text-muted-foreground/70">No tasks here yet</p>
            : items.map((t) => <Card key={t.id} t={t} onOpen={onOpen} {...cardProps} />)}
        </SortableContext>
      </div>
    </div>
  );
}

// Authoritative, optimistic Kanban. Columns are keyed by the STABLE stage id
// (never the display name). Every stage/card mutation updates local state first
// then persists — a rename only changes stage.name and never remaps a card.
export function KanbanBoard({ board, tasks, onOpen, onStagesPersist, members, cats = [], boards = [], canAssign, cfg, catName, onChanged }) {
  const timer = useTimer();
  const running = (id) => !!(timer?.timer?.active && timer.timer.task_id === id && timer.timer.state === "running");
  const boardCfg = cfg || defaultTaskCfg("board");
  const catNameFn = catName || ((id) => cats.find((c) => c.id === id)?.name || "");
  const cardProps = { cfg: boardCfg, catName: catNameFn, cats, members, running };
  const [stages, setStages] = useState(board?.stages || []);   // [{id,name,icon,colour}]
  const [cols, setCols] = useState({});                        // keyed by stage.id
  const [addingStage, setAddingStage] = useState(false);
  const [newStage, setNewStage] = useState("");
  const [renaming, setRenaming] = useState(null);              // stage id being renamed
  const [renameValue, setRenameValue] = useState("");
  const [appearance, setAppearance] = useState(null);   // stage object being styled
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Seed from server data on board switch / genuine task reload.
  const seed = (st, tks) => {
    const map = {}; st.forEach((s) => { map[s.id] = []; });
    const firstId = st[0]?.id;
    [...(tks || [])].sort(byOrder).forEach((t) => {
      const sid = t.stage_id && map[t.stage_id] ? t.stage_id : firstId;
      if (map[sid]) map[sid].push(t);
    });
    setCols(map); setStages(st);
  };
  useEffect(() => { seed(board?.stages || [], tasks); /* eslint-disable-next-line */ }, [tasks, board?.id]);

  const stageIdOf = (taskId) => Object.keys(cols).find((sid) => cols[sid].some((t) => t.id === taskId));
  const nameOf = (sid) => stages.find((s) => s.id === sid)?.name || "";

  const persistCards = async (stageId, ids) => {
    try { await apiFetch(`/tasks/boards/${board.id}/reorder`, { method: "PUT", body: JSON.stringify({ stage_id: stageId, ordered_ids: ids }) }); }
    catch (e) { toast.error("Could not save the board", { description: e.message }); seed(board?.stages || [], tasks); }
  };

  const persistStages = async (next) => {
    try {
      const res = await apiFetch(`/tasks/boards/${board.id}`, { method: "PUT", body: JSON.stringify({ name: board.name, stages: next }) });
      onStagesPersist?.(res?.stages || next);
    } catch (e) {
      toast.error("Could not update the board", { description: e.message });
      seed(board?.stages || [], tasks);
    }
  };

  const onDragEnd = ({ active, over }) => {
    if (!over) return;
    const from = stageIdOf(active.id);
    const to = String(over.id).startsWith("col::") ? String(over.id).slice(5) : stageIdOf(over.id);
    if (!from || !to) return;
    setCols((prev) => {
      const next = { ...prev, [from]: [...prev[from]], [to]: from === to ? [...prev[to]] : [...prev[to]] };
      const item = next[from].find((t) => t.id === active.id);
      if (!item) return prev;
      if (from === to) {
        const oldIdx = next[to].findIndex((t) => t.id === active.id);
        const overIdx = next[to].findIndex((t) => t.id === over.id);
        next[to] = arrayMove(next[to], oldIdx, overIdx < 0 ? next[to].length - 1 : overIdx);
      } else {
        next[from] = next[from].filter((t) => t.id !== active.id);
        const overIdx = next[to].findIndex((t) => t.id === over.id);
        next[to].splice(overIdx < 0 ? next[to].length : overIdx, 0, { ...item, stage_id: to, stage: nameOf(to) });
      }
      persistCards(to, next[to].map((t) => t.id));
      if (from !== to) persistCards(from, next[from].map((t) => t.id));
      return next;
    });
  };

  const moveStageBy = (sid, dir) => {
    const i = stages.findIndex((s) => s.id === sid); const j = i + dir;
    if (j < 0 || j >= stages.length) return;
    const next = arrayMove([...stages], i, j);
    setStages(next); persistStages(next);
  };
  const startRename = (s) => { setRenaming(s.id); setRenameValue(s.name); };
  const commitRename = () => {
    const id = renaming; const name = renameValue.trim();
    setRenaming(null);
    const cur = stages.find((s) => s.id === id);
    if (!cur || !name || name === cur.name) return;
    if (stages.some((s) => s.id !== id && s.name === name)) return toast.error("A stage with that name already exists.");
    const next = stages.map((s) => (s.id === id ? { ...s, name } : s));   // identity (id) unchanged — no card remap
    setStages(next);
    persistStages(next);
  };
  const archiveStage = (sid) => {
    if (stages.length <= 1) return toast.error("Keep at least one stage.");
    const next = stages.filter((s) => s.id !== sid); const fallback = next[0];
    setStages(next);
    setCols((prev) => {
      const nc = { ...prev };
      const moved = (nc[sid] || []).map((t) => ({ ...t, stage_id: fallback.id, stage: fallback.name }));
      delete nc[sid]; nc[fallback.id] = [...(nc[fallback.id] || []), ...moved];
      return nc;
    });
    persistStages(next);
  };
  const addStage = () => {
    const name = newStage.trim(); if (!name) return;
    if (stages.some((s) => s.name === name)) return toast.error("A stage with that name already exists.");
    const stage = { id: newId(), name, icon: null, colour: null };
    const next = [...stages, stage];
    setStages(next); setCols((prev) => ({ ...prev, [stage.id]: [] }));
    setNewStage(""); setAddingStage(false);
    persistStages(next);
  };
  const addCardLocal = (stageId, t) => { if (!t) return; setCols((prev) => ({ ...prev, [stageId]: [...(prev[stageId] || []), { ...t, stage_id: stageId, stage: nameOf(stageId) }] })); };
  const saveStageAppearance = async (sid, { icon, colour }) => {
    const next = stages.map((s) => (s.id === sid ? { ...s, icon, colour } : s));   // appearance keyed by stable stage.id
    setStages(next);
    await persistStages(next);
  };

  if (!board) return null;
  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
      <div className="flex items-start gap-4 overflow-x-auto pb-6" data-testid="kanban-board">
        {stages.map((s) => {
          const slug = slugify(s.name);
          return (
            <Column key={s.id} stage={s} items={cols[s.id] || []} onOpen={onOpen} cardProps={cardProps}
              renaming={renaming === s.id} renameValue={renameValue} onRenameChange={setRenameValue} onRenameCommit={commitRename} onRenameCancel={() => setRenaming(null)}
              onAdd={<TaskQuickCreate align="start" defaults={{ board_id: board.id, stage_id: s.id, task_type: "personal" }} members={members} cats={cats} boards={boards} canAssign={canAssign} onCreated={(t) => addCardLocal(s.id, t)}
                trigger={<button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`add-card-${slug}`}><Plus className="h-3.5 w-3.5" /></button>} />}
              menu={<DropdownMenu>
                <DropdownMenuTrigger asChild><button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`stage-menu-${slug}`}><MoreHorizontal className="h-3.5 w-3.5" /></button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem onClick={() => startRename(s)} data-testid={`stage-rename-${slug}`}>Rename stage</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAppearance(s)} data-testid={`stage-appearance-${slug}`}>Edit appearance</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => moveStageBy(s.id, -1)} data-testid={`stage-left-${slug}`}>Move left</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => moveStageBy(s.id, 1)} data-testid={`stage-right-${slug}`}>Move right</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => archiveStage(s.id)} data-testid={`stage-archive-${slug}`}>Archive stage</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>} />
          );
        })}
        <div className="w-44 shrink-0 pt-1">
          {addingStage ? (
            <div className="flex gap-1.5">
              <Input autoFocus value={newStage} onChange={(e) => setNewStage(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addStage(); if (e.key === "Escape") { setAddingStage(false); setNewStage(""); } }} placeholder="Stage name" className="h-9" data-testid="new-stage-input" />
              <Button size="sm" onClick={addStage} data-testid="new-stage-save">Add</Button>
            </div>
          ) : (
            <button onClick={() => setAddingStage(true)} data-testid="add-stage-btn" className="flex w-full items-center gap-1.5 rounded-xl border border-dashed border-border/80 px-3 py-2.5 text-xs font-medium text-muted-foreground hover:bg-muted/40"><Plus className="h-4 w-4" /> Add stage</button>
          )}
        </div>
      </div>
      {appearance && <AppearanceDialog open onOpenChange={(o) => !o && setAppearance(null)} kind="header"
        title={`Stage appearance — ${appearance.name}`} label={appearance.name}
        initial={{ icon: appearance.icon, colour: appearance.colour }}
        onSave={(v) => saveStageAppearance(appearance.id, v)} />}
    </DndContext>
  );
}
